import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import * as logging from 'util/Logging';

import {InaccessibleInfoGraphQL} from '../../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

/**
 * Renders an error banner when a study query fails.
 */
export function StudyQueryErrorBanner(): JSX.Element {
  return (
    <>
      <div className={css(styles.bannerWrapper)}>
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="An error occurred while loading the study data. Refresh the page and try again."
        />
      </div>
    </>
  );
}

/**
 * Renders an error banner when a component is inaccessible.
 * @param inaccessibleInfo Information about the inaccessible component.
 * @param modelType Type of the inaccessible model.
 * @param displayErrorText Text to display in the error banner.
 */
export function StudyInaccessibleContentBanner(props: {
  inaccessibleInfo: InaccessibleInfoGraphQL;
  modelType: string;
  displayErrorText: string;
}): JSX.Element {
  return (
    <>
      <WorkspaceItemInaccessibleGuard inaccessibleInfo={props.inaccessibleInfo} modelType={props.modelType} />
      <div className={css(styles.bannerWrapper)}>
        <InlineBanner bannerType={InlineBannerType.ERROR} text={props.displayErrorText} />
      </div>
    </>
  );
}

/**
 * Logs an error and displays an error banner when a component is missing expected contents.
 * @param parentRenderFunctionName Name of the parent rendering function that failed.
 * @param modelType Type of the missing model.
 * @param parentId ID of the parent model.
 * @param displayErrorText Text to display in the error banner.
 */
export function StudyMissingContentBanner(props: {
  parentRenderFunctionName: string;
  modelType: string;
  parentId: string;
  displayErrorText: string;
}): JSX.Element {
  const parentId = props.parentId;
  useEffect(() => {
    logging.warn(`Failed to render ${props.parentRenderFunctionName} due to missing ${props.modelType}`, {
      extra: {parentId},
    });
  });
  return (
    <div className={css(styles.bannerWrapper)}>
      <InlineBanner bannerType={InlineBannerType.ERROR} text={props.displayErrorText} />
    </div>
  );
}

const styles = StyleSheet.create({
  bannerWrapper: {
    paddingBottom: 10,
  },
});
